import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import * as voicifyApi from '../../../api';
import ApplicationModel from '../../../models/applications/api/ApplicationModel';
import ApplicationContainer from '../../../state/containers/ApplicationContainer';
import Button from '../../../components/general/Button';
import PageError from '../../../components/general/PageError';
import StyledDropdown from '../../applicationSettings/components/customAssistantConfigurations/StyledDropdown';
import { color_defaults_white, color_shades_dark, color_highlights_blue } from '../../../constants/colors';
import { OutboundCallFeatureFlagId } from '../../../constants/featureFlagIds';
import { FoodOrderingGenAIModelSettingId } from '../../../constants/featureFlagSettingIds';
import { GenAIModels } from '../../../constants/genAiModels';

// Number of applications updated in parallel when applying a setting
const UPDATE_BATCH_SIZE = 5;

const CHECKBOX_OPTIONS = [
    { label: 'Enabled', value: 'true' },
    { label: 'Disabled', value: 'false' }
];

interface BulkFeatureFlagSettingsPanelProps {
    organizationId: string;
    applicationContainer: ApplicationContainer;
}

export const getApplicationFeatureFlagSettingValue = (app: ApplicationModel, featureFlagId: string, featureFlagSettingId: string): string | undefined => {
    // Application settings may carry the schema id as featureFlagSettingId, with id being the record id
    return app?.featureFlags?.find(f => f.id === featureFlagId)?.featureFlagSettings
        ?.find(s => ((s as any).featureFlagSettingId || s.id)?.toLowerCase() === featureFlagSettingId?.toLowerCase())?.value;
};

const BulkFeatureFlagSettingsPanel: React.FC<BulkFeatureFlagSettingsPanelProps> = ({ organizationId, applicationContainer }) => {
    const [selectedFeatureFlagId, setSelectedFeatureFlagId] = useState<string>('');
    const [selectedSettingId, setSelectedSettingId] = useState<string>('');
    const [newValue, setNewValue] = useState<string>('');
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [selectedApps, setSelectedApps] = useState<Set<string>>(new Set());
    const [isApplying, setIsApplying] = useState(false);
    const [logs, setLogs] = useState<string[]>([]);
    const [errors, setErrors] = useState<string[]>([]);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    useEffect(() => {
        applicationContainer.loadFeatureFlags();
    }, [organizationId]);

    const orgApps = applicationContainer.state.baseOrgApplications ?? [];
    const featureFlagDefinitions = applicationContainer.state.featureFlags ?? [];

    // Only offer feature flags that are enabled on at least one app in this organization
    const availableFeatureFlags = featureFlagDefinitions
        .filter(flag => flag.featureFlagSettings?.length > 0)
        .map(flag => ({
            flag,
            appCount: orgApps.filter(app => app.featureFlags?.some(f => f.id === flag.id)).length
        }))
        .filter(f => f.appCount > 0);

    const selectedFeatureFlag = featureFlagDefinitions.find(f => f.id === selectedFeatureFlagId);
    const settingOptions = [...(selectedFeatureFlag?.featureFlagSettings ?? [])].sort((a, b) => (a.renderOrder ?? 0) - (b.renderOrder ?? 0));
    const selectedSetting = settingOptions.find(s => s.id === selectedSettingId);
    const isDropdownSetting = selectedSetting?.type === 'checkbox' || selectedSetting?.id === FoodOrderingGenAIModelSettingId;
    // Text settings may be intentionally cleared, but dropdown settings need one of their options
    const hasValidValue = !!selectedSetting && (!isDropdownSetting || newValue !== '');

    // Default to Outbound Calling when it is available, otherwise the first available flag
    useEffect(() => {
        if (selectedFeatureFlagId && availableFeatureFlags.some(f => f.flag.id === selectedFeatureFlagId)) return;
        const defaultFlag = availableFeatureFlags.find(f => f.flag.id === OutboundCallFeatureFlagId) ?? availableFeatureFlags[0];
        if (defaultFlag) {
            handleFeatureFlagChange(defaultFlag.flag.id);
        }
    }, [availableFeatureFlags.map(f => f.flag.id).join(',')]);

    const eligibleApps = orgApps.filter(app => app.featureFlags?.some(f => f.id === selectedFeatureFlagId));
    const filteredApps = eligibleApps.filter(app => !searchTerm.trim() || app.name?.toLowerCase().includes(searchTerm.trim().toLowerCase()));
    const selectedEligibleApps = eligibleApps.filter(app => selectedApps.has(app.id));

    const resetResults = () => {
        setErrors([]);
        setSuccessMessage(null);
    };

    const handleFeatureFlagChange = (featureFlagId: string) => {
        const flag = featureFlagDefinitions.find(f => f.id === featureFlagId);
        const firstSetting = [...(flag?.featureFlagSettings ?? [])].sort((a, b) => (a.renderOrder ?? 0) - (b.renderOrder ?? 0))[0];
        setSelectedFeatureFlagId(featureFlagId);
        setSelectedSettingId(firstSetting?.id ?? '');
        setNewValue('');
        setSelectedApps(new Set());
        resetResults();
    };

    const handleSettingChange = (settingId: string) => {
        setSelectedSettingId(settingId);
        setNewValue('');
        resetResults();
    };

    const toggleAppSelection = (appId: string) => {
        const newSelected = new Set(selectedApps);
        if (newSelected.has(appId)) {
            newSelected.delete(appId);
        } else {
            newSelected.add(appId);
        }
        setSelectedApps(newSelected);
    };

    const toggleSelectAll = () => {
        if (selectedEligibleApps.length === eligibleApps.length) {
            setSelectedApps(new Set());
        } else {
            setSelectedApps(new Set(eligibleApps.map(app => app.id)));
        }
    };

    const selectVisibleApps = () => {
        setSelectedApps(new Set(filteredApps.map(app => app.id)));
    };

    const selectAppsWithDifferentValue = () => {
        setSelectedApps(new Set(eligibleApps
            .filter(app => (getApplicationFeatureFlagSettingValue(app, selectedFeatureFlagId, selectedSettingId) ?? '') !== newValue)
            .map(app => app.id)));
    };

    const applySetting = async () => {
        if (!hasValidValue || selectedEligibleApps.length === 0) return;

        const confirmed = window.confirm(
            `Set "${selectedSetting.name}" to "${newValue}" on ${selectedEligibleApps.length} application(s)? This takes effect immediately.`
        );
        if (!confirmed) return;

        resetResults();
        setIsApplying(true);
        const newLogs: string[] = [`Updating "${selectedSetting.name}" to "${newValue}" on ${selectedEligibleApps.length} application(s)...`];
        setLogs([...newLogs]);

        // Only the selected setting is sent; the API merges it with the app's other settings
        const requestPayload: UpdateFeatureFlagSettings = {
            featureFlagId: selectedFeatureFlagId,
            featureFlagSettings: [{
                id: selectedSetting.id,
                name: selectedSetting.name,
                value: newValue
            }]
        };

        const failedApps: string[] = [];
        for (let i = 0; i < selectedEligibleApps.length; i += UPDATE_BATCH_SIZE) {
            const batch = selectedEligibleApps.slice(i, i + UPDATE_BATCH_SIZE);
            await Promise.all(batch.map(async app => {
                const previousValue = getApplicationFeatureFlagSettingValue(app, selectedFeatureFlagId, selectedSetting.id);
                try {
                    const result = await voicifyApi.updateApplicationFeatureFlagSettings(app.id, requestPayload);
                    if (result.resultType === 'Ok') {
                        newLogs.push(`✓ ${app.name}: "${previousValue ?? ''}" → "${newValue}"`);
                    } else {
                        failedApps.push(app.name);
                        newLogs.push(`✗ ${app.name}: ${result.errors?.join(', ') || 'Update failed'}`);
                    }
                } catch (error) {
                    failedApps.push(app.name);
                    newLogs.push(`✗ ${app.name}: ${error?.toString() ?? 'Update failed'}`);
                }
            }));
            setLogs([...newLogs]);
        }

        // Reload the apps so the current values reflect the update
        await applicationContainer.getBaseApplicationsForOrg(organizationId, true);

        const succeededCount = selectedEligibleApps.length - failedApps.length;
        if (succeededCount > 0) {
            setSuccessMessage(`Updated "${selectedSetting.name}" on ${succeededCount} application(s).`);
        }
        if (failedApps.length > 0) {
            setErrors([`Failed to update ${failedApps.length} application(s): ${failedApps.join(', ')}`]);
        }
        setIsApplying(false);
    };

    const renderValueInput = () => {
        if (!selectedSetting) return null;

        if (isDropdownSetting) {
            return (
                <StyledDropdown
                    label="New Value"
                    fieldName="newValue"
                    placeholder="Select a value"
                    options={selectedSetting.type === 'checkbox' ? CHECKBOX_OPTIONS : GenAIModels}
                    value={newValue}
                    disabled={isApplying}
                    handleOnChange={(fieldName, value) => setNewValue(value as string)}
                />
            );
        }

        return (
            <InputGroup>
                <Label htmlFor="bulk-feature-flag-setting-value">New Value</Label>
                <ValueTextarea
                    id="bulk-feature-flag-setting-value"
                    rows={2}
                    placeholder={selectedSetting.name}
                    value={newValue}
                    disabled={isApplying}
                    onChange={(e) => setNewValue(e.target.value)}
                />
            </InputGroup>
        );
    };

    return (
        <Container>
            <Header>
                <Title>Bulk Feature Settings</Title>
                <Description>
                    Update a feature flag setting on many applications at once, e.g. set "Missed Appointment Lookback Days" for every app with Outbound Calling enabled. Only the selected setting is changed; other settings are left as they are.
                </Description>
            </Header>

            {availableFeatureFlags.length === 0 ? (
                <EmptyState>
                    <EmptyStateText>No applications in this organization have a feature flag with settings enabled.</EmptyStateText>
                </EmptyState>
            ) : (
                <>
                    <SettingsForm>
                        <StyledDropdown
                            label="Feature Flag"
                            fieldName="featureFlagId"
                            options={availableFeatureFlags.map(f => ({ label: `${f.flag.name} (${f.appCount} apps)`, value: f.flag.id }))}
                            value={selectedFeatureFlagId}
                            disabled={isApplying}
                            handleOnChange={(fieldName, value) => handleFeatureFlagChange(value as string)}
                        />
                        <StyledDropdown
                            label="Setting"
                            fieldName="featureFlagSettingId"
                            options={settingOptions.map(s => ({ label: s.name, value: s.id }))}
                            value={selectedSettingId}
                            disabled={isApplying}
                            tooltip={selectedSetting?.description}
                            handleOnChange={(fieldName, value) => handleSettingChange(value as string)}
                        />
                        {renderValueInput()}
                    </SettingsForm>

                    <SearchBar>
                        <SearchInput
                            type="text"
                            placeholder="Search applications by name"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </SearchBar>

                    <ActionsBar>
                        <SelectionControls>
                            <SelectionInfo>
                                <Checkbox
                                    type="checkbox"
                                    checked={eligibleApps.length > 0 && selectedEligibleApps.length === eligibleApps.length}
                                    onChange={toggleSelectAll}
                                    disabled={isApplying}
                                />
                                <span>{selectedEligibleApps.length} of {eligibleApps.length} selected</span>
                            </SelectionInfo>
                            <Button
                                themes={['secondary-small']}
                                text={`Select Visible (${filteredApps.length})`}
                                onClick={selectVisibleApps}
                                disabled={filteredApps.length === 0 || isApplying}
                            />
                            <Button
                                themes={['secondary-small']}
                                text="Select Apps With Different Value"
                                onClick={selectAppsWithDifferentValue}
                                disabled={!selectedSetting || isApplying}
                            />
                        </SelectionControls>
                        <ButtonGroup>
                            <Button
                                themes={['primary']}
                                text={isApplying ? 'Applying...' : `Apply to Selected (${selectedEligibleApps.length})`}
                                onClick={applySetting}
                                disabled={!hasValidValue || selectedEligibleApps.length === 0 || isApplying}
                                loading={isApplying}
                            />
                        </ButtonGroup>
                    </ActionsBar>

                    {logs.length > 0 && (
                        <LogSection>
                            <LogHeader>
                                <span>Update Progress</span>
                                {!isApplying && (
                                    <Button
                                        themes={['secondary-small']}
                                        text="Clear Log"
                                        onClick={() => setLogs([])}
                                    />
                                )}
                            </LogHeader>
                            <LogArea>
                                {logs.map((log, index) => (
                                    <LogLine key={index}>{log}</LogLine>
                                ))}
                            </LogArea>
                        </LogSection>
                    )}

                    <ApplicationsList>
                        {filteredApps.map(app => {
                            const currentValue = getApplicationFeatureFlagSettingValue(app, selectedFeatureFlagId, selectedSettingId);
                            return (
                                <ApplicationCard key={app.id} selected={selectedApps.has(app.id)}>
                                    <CardContent onClick={() => !isApplying && toggleAppSelection(app.id)}>
                                        <Checkbox
                                            type="checkbox"
                                            checked={selectedApps.has(app.id)}
                                            onChange={() => toggleAppSelection(app.id)}
                                            onClick={(e) => e.stopPropagation()}
                                            disabled={isApplying}
                                        />
                                        <ApplicationInfo>
                                            <ApplicationName>{app.name}</ApplicationName>
                                            <CurrentValue>Current: {currentValue?.length > 0 ? currentValue : '(not set)'}</CurrentValue>
                                        </ApplicationInfo>
                                    </CardContent>
                                </ApplicationCard>
                            );
                        })}
                    </ApplicationsList>
                </>
            )}

            {successMessage && (
                <PageError errors={[successMessage]} type="success" />
            )}
            <PageError errors={errors} />
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
`;

const Header = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const Title = styled.h2`
    margin: 0;
    font-size: 24px;
    font-weight: 600;
`;

const Description = styled.p`
    margin: 0;
    color: #666;
    font-size: 14px;
`;

const EmptyState = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 48px 24px;
    background: ${color_defaults_white};
    border: 1px solid ${color_shades_dark};
    border-radius: 8px;
`;

const EmptyStateText = styled.p`
    color: #666;
    font-size: 14px;
    margin: 0;
`;

const SettingsForm = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
    gap: 16px;
    align-items: start;
    padding: 16px;
    background: ${color_defaults_white};
    border: 1px solid ${color_shades_dark};
    border-radius: 8px;
`;

const InputGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const Label = styled.label`
    font-size: 14px;
    font-weight: 600;
    color: #333;
`;

const ValueTextarea = styled.textarea`
    padding: 10px 12px;
    font-size: 14px;
    border: 1px solid ${color_shades_dark};
    border-radius: 4px;
    background: ${color_defaults_white};
    color: #333;
    resize: vertical;

    &:focus {
        outline: none;
        border-color: ${color_highlights_blue};
    }

    &:disabled {
        background: #f5f5f5;
        cursor: not-allowed;
    }
`;

const SearchBar = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 16px;
    background: ${color_defaults_white};
    border: 1px solid ${color_shades_dark};
    border-radius: 8px;
`;

const SearchInput = styled.input`
    padding: 12px 16px;
    font-size: 14px;
    border: 1px solid ${color_shades_dark};
    border-radius: 4px;
    background: ${color_defaults_white};
    color: #333;

    &:focus {
        outline: none;
        border-color: ${color_highlights_blue};
        box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.1);
    }

    &::placeholder {
        color: #999;
    }
`;

const ActionsBar = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 16px;
    background: ${color_defaults_white};
    border: 1px solid ${color_shades_dark};
    border-radius: 8px;

    @media (min-width: 1200px) {
        flex-direction: row;
        justify-content: space-between;
        align-items: center;
    }
`;

const SelectionControls = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
`;

const ButtonGroup = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
`;

const SelectionInfo = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    font-size: 14px;
`;

const Checkbox = styled.input`
    width: 18px;
    height: 18px;
    cursor: pointer;
`;

const ApplicationsList = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 16px;
`;

const ApplicationCard = styled.div<{ selected: boolean }>`
    border: 2px solid ${props => props.selected ? color_highlights_blue : color_shades_dark};
    border-radius: 8px;
    background: ${color_defaults_white};
    transition: all 0.2s ease;
    cursor: pointer;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    }
`;

const CardContent = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 16px;
`;

const ApplicationInfo = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    flex: 1;
    min-width: 0;
`;

const ApplicationName = styled.h3`
    margin: 0;
    font-size: 16px;
    font-weight: 600;
`;

const CurrentValue = styled.p`
    margin: 0;
    font-size: 13px;
    color: #666;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const LogSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const LogHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 14px;
    font-weight: 600;
    color: #333;
`;

const LogArea = styled.div`
    max-height: 300px;
    overflow-y: auto;
    padding: 16px;
    background: #1e1e1e;
    border-radius: 8px;
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', 'Consolas', monospace;
    font-size: 12px;
`;

const LogLine = styled.div`
    color: #d4d4d4;
    line-height: 1.6;
    white-space: pre-wrap;
    word-break: break-word;
`;

export default BulkFeatureFlagSettingsPanel;
